/**
 * Buying AI credits, from anywhere.
 *
 * WHY THIS EXISTS: until now a retail user had no way to buy AI credits at all.
 * Every purchase entry point (`AskCardAlethos`, `AskAssistant`,
 * `HealthReportCard`) rendered its SKU buttons ONLY inside a `limitReached`
 * branch — so you could buy credits only after running out. Worse, all three
 * live on InvestorHome / MarketScreen, and a user with no holdings is parked on
 * the day-zero screen, which renders none of them. A brand-new retail user was
 * therefore in a closed loop: no credits visible, no way to buy, no way out.
 *
 * The nav is the one surface present on every screen including day zero, so the
 * affordance belongs here.
 *
 * NOT the same pill as `CreditsPill` in AppNav. Two distinct wallets share the
 * word "credits" and confusing them is a real support cost:
 *   · BRIEFING credits — ~Rs 100 each, spent generating equity briefings, and
 *     IFA-only (every generation path is role-gated). That is `CreditsPill`.
 *   · AI credits       — ~Rs 5 each, spent on Ask / health reports. Everyone
 *     has these. That is this component.
 * So this one is deliberately labelled "AI" and never just "credits".
 */
import { useCallback, useEffect, useState, useRef } from "react";
import { useAuth } from "@/auth";
import { useFocusTrap } from "@/hooks/useFocusTrap";
import {
  getAiBudget,
  startAiTopup,
  topupOptionsFor,
  type AiBudget,
  type AiTopupSku,
} from "@/lib/api";

export function AiCreditsPill() {
  // F11: keep Tab inside this dialog and hand focus back on close.
  const _dlgRef = useRef<HTMLDivElement>(null);
  useFocusTrap(_dlgRef, true);

  const { profile, user } = useAuth();
  const [budget, setBudget] = useState<AiBudget | null>(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState<AiTopupSku | null>(null);
  const [err, setErr] = useState<string | null>(null);

  // IFAs already carry the briefing-credits pill; a second one beside it reads
  // as duplication rather than as a different wallet. They still reach AI
  // top-ups from the Ask card. Everyone else gets it here.
  const show = !!user && profile?.role !== "ifa";

  useEffect(() => {
    if (!show) return;
    let cancelled = false;
    getAiBudget()
      .then((b) => { if (!cancelled) setBudget(b); })
      .catch(() => { /* a nav pill must never break the page */ });
    return () => { cancelled = true; };
  }, [show]);

  const buy = useCallback(async (sku: AiTopupSku) => {
    setBusy(sku);
    setErr(null);
    try {
      // Redirects to PayU; credits land on the webhook, never from the client.
      await startAiTopup(sku);
    } catch {
      setErr("Could not start the payment. Please try again.");
      setBusy(null);
    }
  }, []);

  if (!show) return null;
  // Never assert a balance we have not loaded — rendering `?? 0` showed a false
  // "0 AI credits" during hydration and drove support tickets (audit P1-4b).
  if (!budget) return null;

  const left = budget.remaining_credits;
  const low = left <= 0;

  return (
    <div style={{ position: "relative", marginRight: 10 }}>
      <button
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="dialog"
        aria-expanded={open}
        title={low ? "Out of AI credits — buy more" : "AI credits — buy more"}
        className="row gap-2 center"
        style={{
          background: low ? "var(--warn-bg)" : "var(--paper-2)",
          color: low ? "var(--warn-text)" : "var(--ink-mid)",
          border: "1px solid var(--line)",
          padding: "5px 11px",
          borderRadius: 999,
          fontFamily: "var(--mono)",
          fontSize: 12.5,
          letterSpacing: ".06em",
          textTransform: "uppercase",
          cursor: "pointer",
          minHeight: 32,
          gap: 5,
        }}
      >
        <span>{left}</span>
        <span>AI credits</span>
      </button>

      {open && (
        <>
          {/* Click-away. Sits behind the panel, covers the viewport. */}
          <div
            onClick={() => setOpen(false)}
            style={{ position: "fixed", inset: 0, zIndex: 40 }}
          />
          <div
            role="dialog" ref={_dlgRef} tabIndex={-1}
            aria-label="Buy AI credits"
            style={{
              position: "absolute", right: 0, top: "calc(100% + 8px)",
              zIndex: 41, width: 268, padding: 14,
              background: "var(--paper-1)", border: "1px solid var(--line)",
              borderRadius: 12, boxShadow: "0 12px 32px rgba(0,0,0,0.16)",
            }}
          >
            <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 2 }}>
              Buy AI credits
            </div>
            <div style={{ fontSize: 12, color: "var(--ink-mid)", marginBottom: 10 }}>
              {left > 0
                ? `${left} left. Purchased credits carry over.`
                : "You're out of AI credits. Purchased credits carry over."}
            </div>

            <div style={{ display: "grid", gap: 6 }}>
              {topupOptionsFor(profile?.role).map(([sku, label]) => (
                <button
                  key={sku}
                  className="btn btn-primary"
                  disabled={busy !== null}
                  onClick={() => { void buy(sku); }}
                  style={{ padding: "9px 12px", fontSize: 13, width: "100%" }}
                >
                  {busy === sku ? "Opening payment…" : label}
                </button>
              ))}
            </div>

            {err && (
              <div style={{ marginTop: 8, fontSize: 12, color: "var(--warn-text)" }}>
                {err}
              </div>
            )}
            <div style={{ marginTop: 10, fontSize: 11, color: "var(--ink-300)" }}>
              Used for Ask and health reports. Importing holdings is free.
            </div>
          </div>
        </>
      )}
    </div>
  );
}
